// react_frontend/src/Components/Modules/UserSetting/SalaryList.js
import React, { useEffect, useState, useMemo, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Link } from "react-router-dom";

import Swal from "sweetalert2";

// RSuite Components
import {
  Button,
  SelectPicker,
  Badge,
  Loader,
  Message,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaDollarSign,
  FaEdit,
  FaTrashAlt,
  FaCalendarAlt,
  FaUserTie,
  FaHistory,
  FaPlus,
} from "react-icons/fa";
import moment from "moment";

const SalaryList = () => {
  const {
    salaries,
    loadingSalaries,
    removeSalaryFromList,
    startSalaryPolling,
    stopSalaryPolling,
  } = useContext(MotherContext);

  const [filterType, setFilterType] = useState("salary"); // 'salary', 'monthwise', 'employeewise'
  const [monthFilter, setMonthFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");

  const isAdminOrAccount = ["1", "2", "3"].includes(localStorage.getItem("who_am_i"));

  // Fetch Data
  useEffect(() => {
    startSalaryPolling();
    return () => stopSalaryPolling();
  }, [startSalaryPolling, stopSalaryPolling]);

  // Derived Options: Unique Months
  const uniqueMonths = useMemo(() => {
    const months = [...new Map(salaries.map((item) => [item.month, item])).values()];
    return months
      .sort((a, b) => moment(b.month).diff(moment(a.month)))
      .map((item) => ({
        label: moment(item.month).format("MMMM YYYY"),
        value: item.month,
      }));
  }, [salaries]);

  // Derived Options: Unique Employees
  const uniqueEmployees = useMemo(() => {
    const employees = [...new Map(salaries.map((item) => [item.employee_id, item])).values()];
    return employees.map((e) => ({
      label: e?.employee_name,
      value: e?.employee_id,
      designation: e?.employee_designation,
    }));
  }, [salaries]);

  // Filtered Data
  const filteredData = useMemo(() => {
    if (filterType === "monthwise" && monthFilter) {
      return salaries.filter((item) => item.month === monthFilter);
    }
    if (filterType === "employeewise" && employeeFilter) {
      return salaries.filter((item) => item.employee_id === employeeFilter);
    }
    return salaries;
  }, [salaries, filterType, monthFilter, employeeFilter]);

  // Handle Delete
  const handleDelete = (salary) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this salary record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.delete(`/add_salary/${salary.id}`);
          removeSalaryFromList(salary.id);
          toaster.push(
            <Message type="success" duration={3000}>
              Salary record deleted successfully!
            </Message>,
            { placement: "topEnd" }
          );
        } catch (err) {
          toaster.push(
            <Message type="error" duration={5000}>
              Failed to delete salary.
            </Message>,
            { placement: "topEnd" }
          );
        }
      }
    });
  };

  // Columns
  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "60px",
      center: true,
    },
    {
      name: "Employee",
      selector: (row) => row.employee_name,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div>
          <div className="fw-medium">{row.employee_name}</div>
          <small className="text-muted">{row.employee_designation}</small>
        </div>
      ),
    },
    {
      name: "(Salary + Bonus + Other)-Deduction",
      width: "400px",
      cell: (row) => {
        const total = (row.gross_salary + row.festival_allowance + row.other) - (row.deduction || 0);
        return (
          <div className="small">
            <div>
              ({row.gross_salary} + {row.festival_allowance} + {row.other}) - {row.deduction} = {" "}
              <strong className="text-success">{total.toLocaleString()}</strong>
            </div>
          </div>
        );
      },
    },
    {
      name: "Insertion Date",
      selector: (row) => row.insertion_date,
      sortable: true,
      width: "180px",
      cell: (row) => moment(row.insertion_date).format("MMM DD YYYY"),
    },
    {
      name: "Month-Year",
      selector: (row) => row.month,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <Badge
          content={moment(row.month).format("MMM YYYY")}
          style={{ fontSize: "0.85em" }}
        />
      ),
    },
    {
      name: "Note",
      selector: (row) => row.note,
      width: "250px",
      wrap: true,
      cell: (row) => (
        <small className="text-truncate d-block" style={{ maxWidth: 120 }}>
          {row.note || "—"}
        </small>
      ),
    },
    // {
    //   name: "Created / Updated By",
    //   width: "160px",
    //   cell: (row) => (
    //     <div>
    //       <div className="fw-medium">{row.creator_name}</div>
    //       <small className="text-muted">
    //         Updated: {row.updator_name || "—"}
    //       </small>
    //     </div>
    //   ),
    // },
    // {
    //   name: "Timestamp",
    //   width: "180px",
    //   cell: (row) => (
    //     <div className="small">
    //       <div>
    //         <strong>Created:</strong>{" "}
    //         {moment(row.created_at).format("MMM DD, h:mm A")}
    //       </div>
    //       <div>
    //         <strong>Updated:</strong>{" "}
    //         {row.updated_at === row.created_at ? (
    //           "—"
    //         ) : (
    //           moment(row.updated_at).format("MMM DD, h:mm A")
    //         )}
    //       </div>
    //     </div>
    //   ),
    // },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1 justify-content-center">
          {/* Edit Button */}
          <Link
            to={`/edit_salary/${row.id}`}
            style={{ textDecoration: "none" }}
            title="Edit salary record"
          >
            <Button size="sm" color="blue" startIcon={<FaEdit />}>
              Edit
            </Button>
          </Link>

          {/* Delete Button */}
          <Button
            size="sm"
            color="red"
            startIcon={<FaTrashAlt />}
            onClick={() => handleDelete(row)}
            title="Delete salary record"
          >
            Delete
          </Button>
        </div>
      ),
      width: "160px",
      center: true,
    },
  ];

  // Custom Styling
  const customStyles = {
    head: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "70px",
      },
    },
    cells: {
      style: {
        padding: "12px 16px",
      },
    },
  };

  return (
    <>
      <Breadcrumb title="Salary Management" />

      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <FaDollarSign className="me-2" />
          Employee Salaries
        </h5>

        <div className="d-flex gap-2 w-100 w-md-auto align-items-center">
          {/* Add Salary Button */}
          {isAdminOrAccount && (
            <Link to="/add_salary" style={{ textDecoration: "none" }} title="Add new salary">
              <Button
                appearance="primary"
                startIcon={<FaPlus />}
                className="d-flex align-items-center custom-bg"
              >
                Add Salary
              </Button>
            </Link>
          )}

          {/* Month Filter */}
          <SelectPicker
            placeholder="All Months"
            data={uniqueMonths}
            value={monthFilter}
            onChange={(value) => {
              setFilterType("monthwise");
              setMonthFilter(value);
            }}
            onClean={() => {
              setFilterType("salary");
              setMonthFilter("");
            }}
            style={{ width: 180 }}
            searchable={false}
            title="Filter by month"
          />

          {/* Employee Filter */}
          <SelectPicker
            placeholder="All Employees"
            data={uniqueEmployees}
            value={employeeFilter}
            onChange={(value) => {
              setFilterType("employeewise");
              setEmployeeFilter(value);
            }}
            onClean={() => {
              setFilterType("salary");
              setEmployeeFilter("");
            }}
            labelKey="label"
            valueKey="value"
            style={{ width: 200 }}
            title="Filter by employee"
          />
        </div>
      </div>

      <DataTable
        keyField="id"
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[100, 200, 300, 500]}
        progressPending={loadingSalaries}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading salaries...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <FaHistory size={48} className="mb-3 text-secondary" />
            <h5>No salary records found</h5>
            <p>Try adjusting your filters or add a new salary entry.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />
    </>
  );
};


export default SalaryList;